import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Check,
  Info,
  Loader2,
  Sparkles,
  AlertCircle,
  XCircle,
  Tag,
} from "lucide-react";
import { RefreshCWIcon } from "../../../components/icons";
import { hasWhitespace, stripWhitespace } from "../utils/entityName";
import {
  INPUT_CLASS,
  INPUT_ERROR_CLASS,
  LABEL_CLASS,
  CARD_CLASS,
} from "../utils/styles";

const VALIDATE_DEBOUNCE_MS = 700;
const SPACE_NOTICE_MS = 3500;

function EntityNamePicker({
  folderName,
  onFolderNameChange,
  entitySuggestions,
  isSuggestionsLoading,
  suggestionsError,
  onFetchSuggestions,
  isEntityNameValidating,
  entityNameValidation,
  onValidateEntityName,
  canFetchSuggestions = true,
  disabled = false,
  error,
  t,
}) {
  const [isManualInput, setIsManualInput] = useState(false);
  const [showSpaceNotice, setShowSpaceNotice] = useState(false);

  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const spaceNoticeTimerRef = useRef(null);
  const prevFolderNameRef = useRef(folderName);
  const autoSelectedRef = useRef(null);
  const hasUserInteractedRef = useRef(Boolean(folderName?.trim()));

  const isRefreshDisabled =
    disabled || isSuggestionsLoading || !canFetchSuggestions;
  const containsSpaces = hasWhitespace(folderName?.trim());

  const validateName = useCallback(
    (name) => {
      if (!hasWhitespace(name?.trim())) onValidateEntityName(name);
    },
    [onValidateEntityName],
  );

  const flashSpaceNotice = useCallback(() => {
    setShowSpaceNotice(true);
    if (spaceNoticeTimerRef.current) clearTimeout(spaceNoticeTimerRef.current);
    spaceNoticeTimerRef.current = setTimeout(
      () => setShowSpaceNotice(false),
      SPACE_NOTICE_MS,
    );
  }, []);

  useEffect(
    () => () => {
      if (spaceNoticeTimerRef.current)
        clearTimeout(spaceNoticeTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (
      isSuggestionsLoading ||
      !entitySuggestions ||
      entitySuggestions.length === 0
    )
      return;

    const first = entitySuggestions[0];
    if (
      !isManualInput &&
      !hasUserInteractedRef.current &&
      first &&
      autoSelectedRef.current !== first
    ) {
      autoSelectedRef.current = first;
      onFolderNameChange(first);
      prevFolderNameRef.current = first;
      validateName(first);
    }
  }, [
    entitySuggestions,
    isSuggestionsLoading,
    isManualInput,
    onFolderNameChange,
    validateName,
  ]);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    const trimmed = folderName?.trim();
    if (!trimmed) {
      prevFolderNameRef.current = "";
      return;
    }
    if (prevFolderNameRef.current?.trim() === trimmed) return;
    prevFolderNameRef.current = trimmed;

    debounceTimerRef.current = setTimeout(() => {
      validateName(trimmed);
    }, VALIDATE_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [folderName, validateName]);

  const handleInputKeyDown = useCallback(
    (e) => {
      if (e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        flashSpaceNotice();
      }
    },
    [flashSpaceNotice],
  );

  const handleInputChange = useCallback(
    (e) => {
      hasUserInteractedRef.current = true;
      setIsManualInput(true);
      const raw = e.target.value;
      if (!hasWhitespace(raw)) {
        onFolderNameChange(raw);
        return;
      }
      const caret = e.target.selectionStart ?? raw.length;
      const nextCaret = stripWhitespace(raw.slice(0, caret)).length;
      onFolderNameChange(stripWhitespace(raw));
      flashSpaceNotice();
      requestAnimationFrame(() => {
        const input = inputRef.current;
        if (input && document.activeElement === input) {
          input.setSelectionRange(nextCaret, nextCaret);
        }
      });
    },
    [onFolderNameChange, flashSpaceNotice],
  );

  const handleSuggestionClick = useCallback(
    (name) => {
      hasUserInteractedRef.current = true;
      setIsManualInput(false);
      autoSelectedRef.current = name;
      onFolderNameChange(name);
      prevFolderNameRef.current = name;
      validateName(name);
    },
    [onFolderNameChange, validateName],
  );

  const handleRefresh = useCallback(() => {
    if (isRefreshDisabled) return;
    onFetchSuggestions();
  }, [isRefreshDisabled, onFetchSuggestions]);

  const isAvailable = entityNameValidation?.available === true;
  const isTaken = entityNameValidation?.available === false;
  const hasValidation = entityNameValidation !== null;
  const showSuggestions =
    entitySuggestions && entitySuggestions.length > 0 && !isSuggestionsLoading;

  const getInputStateClass = () => {
    if (error || containsSpaces) return INPUT_ERROR_CLASS;
    if (isEntityNameValidating)
      return "border-cfButton focus:ring-cfPrimary/30";
    if (isAvailable)
      return "border-cfActiveText focus:border-cfActiveText focus:ring-cfActiveRing";
    if (isTaken)
      return "border-cfStatusBlockedText focus:border-cfStatusBlockedText focus:ring-cfStatusBlockedText/20";
    return "";
  };

  return (
    <section
      id="cert-onboarding-entity-name-picker"
      className={`${CARD_CLASS} p-4 sm:p-6 mb-4 transition-all duration-300 ${disabled ? "opacity-70 pointer-events-none cursor-not-allowed" : ""}`}
      aria-labelledby="entity-name-picker-heading"
    >
      <div className="mb-4 pb-4 border-b border-cfCardBorder">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div className="shrink-0 w-8 h-8 rounded-lg bg-cfPrimary flex items-center justify-center">
              <Tag
                className="w-4 h-4 text-cfSecondary"
                strokeWidth={2}
                aria-hidden
              />
            </div>
            <div>
              <h2
                id="entity-name-picker-heading"
                className="text-base font-semibold text-cfHeadingText leading-tight"
              >
                {t("certificateOnboarding:entity_name_label")}
              </h2>
              <p className="text-xs text-cfGlobalText mt-0.5">
                {t("certificateOnboarding:entity_name_subtitle")}
              </p>
            </div>
          </div>

          {(showSuggestions || isSuggestionsLoading) && (
            <button
              type="button"
              id="cert-entity-refresh-suggestions"
              onClick={handleRefresh}
              disabled={isRefreshDisabled}
              title={t("certificateOnboarding:entity_name_refresh_suggestions")}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-cfCardBorder text-cfGlobalText bg-cfSecondary hover:bg-cfCardHover hover:border-cfPrimary/30 hover:text-cfPrimary transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary disabled:opacity-40 disabled:cursor-not-allowed"
              aria-busy={isSuggestionsLoading}
            >
              {isSuggestionsLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
              ) : (
                <RefreshCWIcon size={14} className="inline-flex" aria-hidden />
              )}
              <span className="hidden sm:inline">
                {t("certificateOnboarding:entity_name_refresh_suggestions")}
              </span>
            </button>
          )}
        </div>
      </div>
      <div>
        {isSuggestionsLoading && !showSuggestions && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-3.5 h-3.5 text-cfPrimary" aria-hidden />
              <p className="text-xs font-medium text-cfPrimary uppercase tracking-wider">
                {t("certificateOnboarding:entity_name_loading_suggestions")}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="h-9 rounded-lg bg-cfCardHover animate-pulse"
                  style={{ width: `${90 + i * 22}px` }}
                />
              ))}
            </div>
          </div>
        )}

        {suggestionsError && !isSuggestionsLoading && (
          <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-cfStatusBlockedText/5 border border-cfStatusBlockedText/20">
            <AlertCircle
              className="w-4 h-4 text-cfStatusBlockedText shrink-0"
              aria-hidden
            />
            <p className="text-sm text-cfStatusBlockedText flex-1">
              {t("certificateOnboarding:entity_name_suggestions_failed")}
            </p>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isRefreshDisabled}
              className="shrink-0 text-xs font-medium text-cfPrimary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary disabled:opacity-40"
            >
              {t("certificateOnboarding:entity_name_refresh_suggestions")}
            </button>
          </div>
        )}

        {showSuggestions && (
          <div
            className="mb-4"
            role="group"
            aria-label={t(
              "certificateOnboarding:entity_name_suggestions_title",
            )}
          >
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-3.5 h-3.5 text-cfPrimary" aria-hidden />
              <p className="text-xs font-semibold text-cfPrimary uppercase tracking-wider">
                {t("certificateOnboarding:entity_name_suggestions_title")}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {entitySuggestions.map((name) => {
                const isSelected = folderName?.trim() === name;
                const isThisValidated = isSelected && isAvailable;
                const isThisValidating = isSelected && isEntityNameValidating;
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => handleSuggestionClick(name)}
                    disabled={disabled}
                    aria-pressed={isSelected}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-sm font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary cursor-pointer active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-60 ${
                      isThisValidated
                        ? "border-cfActiveBorder bg-cfActiveBg text-cfActiveText shadow-sm"
                        : isSelected
                          ? "border-cfPrimaryBorder bg-cfPrimaryMuted text-cfPrimary shadow-sm"
                          : "border-cfCardBorder bg-cfSecondary text-cfHeadingText hover:bg-cfCardHover hover:border-cfPrimaryBorder hover:text-cfPrimary"
                    }`}
                  >
                    {isThisValidating && (
                      <Loader2
                        className="w-3.5 h-3.5 animate-spin shrink-0"
                        aria-hidden
                      />
                    )}
                    {!isThisValidating && isThisValidated && (
                      <Check
                        className="w-3.5 h-3.5 shrink-0"
                        strokeWidth={2.5}
                        aria-hidden
                      />
                    )}
                    <span className="truncate max-w-[14rem]">{name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div>
          <label
            htmlFor="cert-onboarding-entity-name-input"
            className={`flex items-center gap-2 mb-1.5 ${LABEL_CLASS}`}
          >
            {t("certificateOnboarding:entity_name_input_label")}
            <span className="text-cfStatusBlockedText" aria-hidden="true">
              *
            </span>
          </label>
          <p className="text-xs text-cfGlobalText mb-2.5">
            {t("certificateOnboarding:entity_name_custom_hint")}
          </p>
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              id="cert-onboarding-entity-name-input"
              value={folderName}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              aria-invalid={containsSpaces || Boolean(error) || undefined}
              placeholder={t(
                "certificateOnboarding:entity_name_input_placeholder",
              )}
              className={`${INPUT_CLASS} ${getInputStateClass()} pr-10 transition-all duration-200`}
              autoComplete="off"
              spellCheck="false"
              required
              aria-required="true"
              aria-describedby="entity-name-validation-msg"
              disabled={disabled}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              {isEntityNameValidating && (
                <Loader2
                  className="w-4 h-4 animate-spin text-cfPrimary"
                  aria-hidden
                />
              )}
              {!isEntityNameValidating && isAvailable && (
                <div className="w-7 h-7 rounded-full bg-cfActiveLightBg flex items-center justify-center">
                  <Check
                    className="w-5 h-5 text-cfActiveText"
                    strokeWidth={3}
                    aria-hidden
                  />
                </div>
              )}
              {!isEntityNameValidating && isTaken && (
                <div className="w-5 h-5 rounded-full bg-cfStatusBlockedText/10 flex items-center justify-center">
                  <XCircle
                    className="w-3.5 h-3.5 text-cfStatusBlockedText"
                    strokeWidth={2}
                    aria-hidden
                  />
                </div>
              )}
            </div>
          </div>
          <div
            id="entity-name-validation-msg"
            className="mt-2.5"
            aria-live="polite"
          >
            {containsSpaces && (
              <div
                id="entity-name-spaces-error"
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-cfStatusBlockedText/5 border border-cfStatusBlockedText/20 animate-fadeIn"
                role="alert"
              >
                <XCircle
                  className="w-4 h-4 text-cfStatusBlockedText shrink-0"
                  strokeWidth={2}
                  aria-hidden
                />
                <p className="text-sm font-medium text-cfStatusBlockedText">
                  {t("certificateOnboarding:entity_name_no_spaces_error")}
                </p>
              </div>
            )}
            {!containsSpaces && showSpaceNotice && (
              <div
                id="entity-name-spaces-notice"
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-cfWarningBg border-none animate-fadeIn"
              >
                <Info
                  className="w-4 h-4 text-cfWarningText shrink-0"
                  strokeWidth={2}
                  aria-hidden
                />
                <p className="text-xs font-medium text-cfWarningText">
                  {t("certificateOnboarding:entity_name_no_spaces_notice")}
                </p>
              </div>
            )}
            {!isEntityNameValidating && hasValidation && isTaken && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-cfStatusBlockedText/5 border border-cfStatusBlockedText/20 animate-fadeIn">
                <XCircle
                  className="w-4 h-4 text-cfStatusBlockedText shrink-0"
                  strokeWidth={2}
                  aria-hidden
                />
                <p className="text-sm font-medium text-cfStatusBlockedText">
                  {entityNameValidation.message ||
                    t("certificateOnboarding:entity_name_taken")}
                </p>
              </div>
            )}
            {error && !hasValidation && !containsSpaces && (
              <p
                id="entity-name-error"
                className="text-xs text-cfStatusBlockedText ml-1"
                role="alert"
              >
                {error}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default EntityNamePicker;
